<template>
  <div class="w-full pt-8 px-8 flex flex-col justify-center">
    <h1 class="text-cw-900">{{ title }}</h1>

    <div class="grid grid-cols-1 mt-8">
      <div v-if="addingSubscribers" class="w-full space-y-6">
        <!-- Email -->
        <div class="flex flex-col space-y-1">
          <p class="text-xs italic">
            Subscriber limit:
            <span :class="followerCountFeedback">{{ followerCount }}</span> /
            {{ planLimitations.maxFollowers }}
          </p>
          <h6 v-if="hasEmailError" class="text-red-500 italic">
            {{ emailError }}
          </h6>

          <h6
            v-if="wouldExceedLimit && !settings.isPro"
            class="flex pt-2 items-center space-x-2"
          >
            Click here to upgrade:
            <span class="ml-2"><ProTag /></span>
          </h6>
        </div>

        <div
          :class="[inputWrapperClass, addingSubscribers ? 'h-auto' : 'h-12']"
        >
          <h6 :class="inputLabelClass">
            {{ emailLabel }}
            <span v-if="addingSubscribers" class="italic">required</span>
          </h6>
          <InputEmailMultiple
            v-if="addingSubscribers"
            @input-updated="updateEmails"
          />
        </div>
        <!-- /Email -->
        <!-- /Mail lists -->
      </div>

      <div v-else class="">
        <div class="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="w-4 h-4 mr-2"
          >
            <path
              d="M10 8a3 3 0 100-6 3 3 0 000 6zM3.465 14.493a1.23 1.23 0 00.41 1.412A9.957 9.957 0 0010 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 00-13.074.003z"
            />
          </svg>
          <h3 class="text-lg m-0">{{ email }}</h3>
        </div>

        <!-- Mail lists -->
        <div class="mt-12">
          <InputDropdown
            :selected-options-initial="selectedMailListIds"
            :label="'Mail lists'"
            :required="true"
            :options="getMailListInputOptions()"
            :multiselect="true"
            :margin-bottom="false"
            :border-enabled="true"
            :shadow-enabled="false"
            :response-selection-id="'updateMailLists'"
          />
        </div>
      </div>
    </div>
    <h6 v-if="addingSubscribers" class="px-2 mt-2 italic">
      Type or copy and paste one or more subscriber emails, separated by spaces
      or commas.
    </h6>

    <!-- Actions -->
    <div :class="['grid grid-cols-1', { 'mt-6': !addingSubscribers }]">
      <div class="w-full flex items-center justify-center mt-10">
        <button
          type="button"
          :disabled="!canSubmit || thinking"
          class="btn btn-primary relative w-40 m-0"
          :class="{
            'opacity-25 cursor-not-allowed': !canSubmit,
          }"
          @click="onSubmit"
        >
          <div class="flex justify-center items-center w-full h-full">
            <div
              class="absolute inset-0 w-full flex justify-center items-center z-20"
            >
              <lottie v-if="thinking" :options="loadingAnimationOptions" />
            </div>
            <div
              :class="[
                thinking ? 'invisible' : '',
                'w-full flex items-center justify-center capitalize',
              ]"
            >
              {{ action }}
            </div>
          </div>
        </button>
      </div>
    </div>
    <!-- /Actions -->
  </div>
</template>

<script>
import { EventBus } from '../../../../../../js/plugins/event-bus.js'
import { mapGetters, mapActions } from 'vuex'
import { validateEmail } from '../../../../../../js/util/helper.js'
import InputDropdown from '../../../../../../shared/components/InputDropdown.vue'
import InputEmailMultiple from '../InputEmailMultiple.vue'
import ProTag from '../../../components/ProTag.vue'
import * as loadingAnimation from '../../../../../../shared/components/loading.json'

export default {
  components: {
    InputDropdown,
    InputEmailMultiple,
    ProTag,
  },

  props: {
    config: { default: null },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      thinking: false,
      // form
      action: this.config.action || 'create',
      title: this.config.title || 'Create a new subscriber',
      email: this.config.subscriber?.email || null,
      selectedMailListIds: [], // @todo update
      selectedMailListIdsInitial: [],
      emails: [],
      // styles
      inputWrapperClass: 'rounded relative bg-white border border-truegray-300',
      inputLabelClass:
        'absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-truegray-700',
      inputTextClass:
        'flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300',
    }
  },

  computed: {
    ...mapGetters({
      mailLists: 'careerwise/getMailLists',
      existingSubscribers: 'careerwise/getFollowers',
      getFollowersCountClasses: 'careerwise/getFollowersCountClasses',
      getMailListIdsByFollowerEmail: 'careerwise/getMailListIdsByFollowerEmail',
      getMailListInputOptions: 'careerwise/getMailListInputOptions',
      planLimitations: 'careerwise/getPlanLimitations',
      isOverFollowerLimit: 'careerwise/isOverFollowerLimit',
      settings: 'shared/getSettings',
    }),

    addingSubscribers() {
      return this.action === 'add'
    },

    emailLabel() {
      if (this.addingSubscribers) {
        return 'Subscriber emails'
      }
      return 'Subscriber email'
    },

    emailTrimmed() {
      return this.email?.trim() || null
    },

    isEmailValidated() {
      return validateEmail(this.emailTrimmed)
    },

    isExistingEmail() {
      return this.existingSubscribers.find(
        (subscriber) => subscriber.email?.trim() === this.emailTrimmed,
      )
    },

    isValidEmail() {
      const validated = !!this.emailTrimmed && this.isEmailValidated
      if (this.action === 'create') {
        return !this.isExistingEmail && validated
      }
      return validated
    },

    hasEmailError() {
      return this.emailError && !this.thinking
    },

    wouldExceedLimit() {
      return this.isOverFollowerLimit(this.followerCount)
    },

    emailError() {
      if (this.action === 'create') {
        if (this.isExistingEmail) {
          return 'This subscriber already exists. Please enter a new email.'
        }
      }

      if (this.wouldExceedLimit) {
        let error = 'You have exceeded your plan limit.'
        if (!this.settings.isPro) {
          error += ' Upgrade to Pro to add more subscribers.'
        }
        return error
      }

      // if (!this.isEmailValidated) return 'Please enter a valid email.'

      return ''
    },

    followerCount() {
      const existingEmails = this.existingSubscribers.map((obj) => obj.email)
      const combinedEmails = new Set([...this.emails, ...existingEmails])
      return combinedEmails.size
    },

    followerCountFeedback() {
      return this.getFollowersCountClasses(this.followerCount)
    },

    canSubmit() {
      // note: emails are validated in input component
      const listValid = !!this.selectedMailListIds.length
      const limitsExceeded = this.wouldExceedLimit
      if (this.addingSubscribers) {
        return !!this.emails.length && listValid && !limitsExceeded
      }
      return this.isValidEmail && listValid
    },

    payload() {
      const payload = {
        followers: [],
        activeListId: this.config.list?.id || null,
        modifiedLists: [
          ...this.selectedMailListIds.filter(
            (id) => !this.selectedMailListIdsInitial.includes(id),
          ),
          ...this.selectedMailListIdsInitial.filter(
            (id) => !this.selectedMailListIds.includes(id),
          ),
        ],
      }

      if (this.addingSubscribers) {
        payload.followers = this.emails.map((email) => {
          // we don't want to overwrite any existing lists for
          // this follower, if the follower already exists;
          // so we need to send those list ids through too
          const currLists = this.getMailListIdsByFollowerEmail(email)

          return {
            email,
            mail_lists: [
              ...new Set([...currLists, ...this.selectedMailListIds]),
            ],
          }
        })
      } else {
        payload.followers = [
          {
            email: this.email?.trim(),
            mail_lists: this.selectedMailListIds,
          },
        ]
      }

      // pass through unique mail list ids separately for Hail sync
      const uniqueMailListIds = [
        ...new Set(
          payload.followers.flatMap((follower) => follower.mail_lists),
        ),
      ]
      payload.mail_list_ids = uniqueMailListIds

      return payload
    },
  },

  mounted() {
    this.init()

    EventBus.$on('updateMailLists', (value) => {
      this.$set(this, 'selectedMailListIds', value)
    })
  },

  methods: {
    ...mapActions({
      dispatchUpdateFollowers: 'careerwise/dispatchUpdateFollowers',
    }),

    updateEmails(emails) {
      this.$set(this, 'emails', emails)
    },

    init() {
      // updating existing subscriber
      if (this.action === 'update') {
        let lists = this.config.subscriber?.lists
        if (lists) {
          lists = lists.map((list) => list.id)
          this.$set(this, 'selectedMailListIds', lists)
          this.$set(this, 'selectedMailListIdsInitial', [
            ...this.selectedMailListIds,
          ])
        }
      }

      // add subscriber(s) to existing list
      else if (this.addingSubscribers) {
        const list = this.config.list
        if (list) {
          this.selectedMailListIds.push(list.id)
        }
      }
    },

    async onSubmit() {
      if (this.canSubmit) {
        this.$set(this, 'thinking', true)
        await this.dispatchUpdateFollowers(this.payload)
        this.$set(this, 'thinking', false)
        EventBus.$emit('showModal', false)
      }
    },
  },
}
</script>
